import { DEFAULT_UTC_OFFSET, formatUtcOffsetLabel, normalizeUtcOffset } from '@/utils/date'
import type { PriorityPaymentRow } from '@/Network/Types/Responses/PriorityPaymentsResponses'

export interface PriorityStationTime {
  /** `'18/09 08:00–19:00'` — travessão, como no mockup. */
  text: string
  /**
   * `'UTC-3'`, `'UTC-4'`… — o rótulo que o painel já usa. **Sempre exibido**
   * (Q18: "com o fuso explícito"): quem lê a fila precisa LER o fuso, não
   * deduzi-lo da ausência de selo.
   */
  timezoneLabel: string
  /** `true` = o selo troca o tom neutro pelo de destaque (D1, opção b). */
  isOutsideBrasilia: boolean
}

/**
 * Offset do plantão (`'-04:00'`) em minutos, que é o que `formatUtcOffsetLabel`
 * e a aritmética de data pedem. `normalizeUtcOffset` já cobre as três grafias
 * que o backend persiste (`-03:00`, `-0300`, `-03`) e cai no fuso padrão do
 * produto quando recebe lixo — então aqui nunca nasce `NaN`.
 */
export const priorityOffsetMinutes = (offset: string): number => {
  const normalized = normalizeUtcOffset(offset)
  const sign = normalized.startsWith('-') ? -1 : 1
  const hours = Number(normalized.slice(1, 3))
  const minutes = Number(normalized.slice(4, 6))
  return sign * (hours * 60 + minutes)
}

/**
 * `startLocal`/`endLocal` já são hora de PAREDE no fuso do plantão
 * (`'YYYY-MM-DDTHH:mm:ss'`, sem offset). Passá-los por `new Date()` os leria no
 * fuso do NAVEGADOR e deslocaria o horário de quem abre a tela de outro fuso —
 * o bug que `buildIsoAtOffset` existe para evitar. Por isso aqui é recorte de
 * string, e nada mais.
 */
export const formatPriorityStationTime = (
  row: Pick<PriorityPaymentRow, 'startLocal' | 'endLocal' | 'timezone'>
): PriorityStationTime => {
  const day = row.startLocal.slice(8, 10)
  const month = row.startLocal.slice(5, 7)
  const startHour = row.startLocal.slice(11, 16)
  const endHour = row.endLocal.slice(11, 16)
  const normalized = normalizeUtcOffset(row.timezone)

  return {
    text: `${day}/${month} ${startHour}–${endHour}`,
    timezoneLabel: formatUtcOffsetLabel(priorityOffsetMinutes(row.timezone)),
    isOutsideBrasilia: normalized !== DEFAULT_UTC_OFFSET
  }
}

/** `'2026-09-18'` — o `startDate` do deep link do Dashboard. */
export const priorityStationDateIso = (row: Pick<PriorityPaymentRow, 'startLocal'>): string =>
  row.startLocal.slice(0, 10)
